/* eslint-disable no-unused-vars,arrow-body-style */
/* global G */
import { curry } from 'lib/util'
import modelDelete from 'lib/sequence/model/api/delete'
import modelCreate from 'lib/sequence/model/api/create'
import modelTransform from 'lib/sequence/model/transformer'
import { DialogOptions, showBlockingDialog } from 'app/_shared/events/dialog'
import showToast, { ToastOptions } from 'app/_shared/component/showToast'

type DeleteEventHandlerOptions = {
  /** translations' namespace */
  ns?: string,
  /** translation's key part */
  type?: string,
  /** whether related draft should also be deleted */
  alsoDraft?: boolean,
  /** whether only related draft should be deleted */
  onlyDraft?: boolean,
  /** whether to ignore errors and consider the operation always successful */
  optimistic?: boolean,
  /** handler called if operation is successful (or optimistic is true) */
  onSuccess?: Gaia.AppModule.EventHandler,
}

/**
 * Returns options for the confirmation dialog.
 *
 * @param [options]       configuration options
 * @param [options.ns]    translations' namespace
 * @param [options.type]  model type name
 */
const dialogOptions = ({ ns = 'common', type = 'model' } = {}): DialogOptions => ({
  title: {
    ns,
    key: `dialog.${type}.delete.title`,
    defaultValue: 'Delete',
  },
  text: {
    ns,
    key: `dialog.${type}.delete.text`,
    defaultValue: `Are you sure you want to delete this ${type}?`,
  },
})

/**
 * Returns options for the success toast.
 *
 * @param [options]       configuration options
 * @param [options.ns]    translations' namespace
 * @param [options.type]  model type name
 */
const successToastOptions = ({ ns = 'common', type = 'model' } = {}): ToastOptions => ({
  severity: 'success',
  message: {
    ns,
    key: `toast.${type}.deleteSuccess`,
    defaultValue: `${type} deleted.`,
  },
})

/**
 * Returns options for the error toast.
 *
 * @param [options]       configuration options
 * @param [options.ns]    translations' namespace
 * @param [options.type]  model type name
 */
const errorToastOptions = ({ ns = 'common', type = 'model' } = {}): ToastOptions => ({
  severity: 'error',
  message: {
    ns,
    key: `toast.${type}.deleteError`,
    defaultValue: `${type} could not be deleted. Please, try again later.`,
  },
})

/**
 * Creates a new {@link Draft} object from `model`.
 *
 * @param model
 */
const createDeleteDraft = (model: Gaia.Model.Spec): any => {
  const { api: type } = model[G.PROPS]
  const key = model[G.STATE][G.REF]

  modelCreate(model)
  modelTransform(model)

  const data = model[G.STATE][G.DATA]

  return {
    id: key,
    revision: '0',
    remoteRevision: '0',
    data: {
      ...data,
      key,
      _id: key,
    },
    type,
    action: 'delete',
    save: true,
    submit: true,
    error: null,
  }
}

/**
 * Delete Model Attribute with Drafts
 *
 * After asking for confirmation, attempts to delete the current model attribute from the server,
 * clears the local model attribute and redirects to the route configured in `component`.
 *
 * If `attributeName` is `null`, attempts to delete the main model.
 *
 * @param attributeName name of the model attribute to delete
 * @param [options]     handler configuration object
 */
const deleteAttributeWithDrafts = (
  attributeName: string | null,
  options: DeleteEventHandlerOptions = {},
) => curry(async (
  module: Gaia.AppModule.Spec,
  component: Gaia.Component.Spec,
  event: Gaia.PlatformEvent,
) => {
  const { alsoDraft, onlyDraft, optimistic, onSuccess } = options || {}
  const { drafts } = module[G.ADAPTER][G.PERSISTENCE]
  const model = attributeName ? module[G.MODEL][G.CHILDREN][attributeName] : module[G.MODEL]
  const modelKey = model[G.STATE][G.REF]
  const httpAdapter = model[G.ADAPTER][G.HTTP]
  const online = module[G.ADAPTER][G.SESSION][G.STATE][G.ONLINE]
  const isRemote = !!model[G.CACHE]._rev
  let error: Error | null = null

  /**
   * TODO: translations should come from `component` but our `quickActions` cannot have a _t property.
   */
  const confirmed = await showBlockingDialog(module, null, dialogOptions(options))

  if (confirmed) {
    if (alsoDraft || onlyDraft) {
      try {
        isRemote && !online
          ? await drafts.put(createDeleteDraft(model))
          : await drafts.delete(modelKey)
      } catch (e: any) {
        error = e
        console.error(e)
      }
    }

    if (!onlyDraft && isRemote && online) {
      try {
        const contextOptions = { middleware: ({ error: _, ...rest }) => Object.values(rest) }
        await httpAdapter.withContext(contextOptions, async () => await modelDelete(model))
      } catch (e: any) {
        if (e.code === 404) { // if the item couldn't be found...
          await drafts.delete(modelKey) // ...we delete the local draft...
          error = null // ...and don't consider it an error
        } else {
          console.error(e)
          error = e
        }
      }
    }

    if ((!error || optimistic) && onSuccess) {
      await showToast(module, successToastOptions(options))
      await onSuccess?.(module, component, event)
    } else {
      await showToast(module, errorToastOptions(options))
    }
  }
})

/**
 * Delete Model with Drafts
 *
 * After asking for confirmation, attempts to delete the current model from the server, clears the
 * local model and redirects to the route configured in `component`.
 *
 * @param [options] handler configuration object
 */
const deleteWithDrafts = (
  options: DeleteEventHandlerOptions = {}
) => curry( async (
  module: Gaia.AppModule.Spec,
  component: Gaia.Component.Spec,
  event: Gaia.PlatformEvent,
) => {
  return await deleteAttributeWithDrafts(null, options)(module, component, event)
})

export {
  deleteWithDrafts as default,
  deleteAttributeWithDrafts,
}
